/**
 * Partner administration API service
 *
 * Administrator-only. Every call is refused by Cerbos for any other role, so
 * these are not defensive about who is calling.
 */

import { z } from 'zod'
import { config } from '@/config'

const API_BASE_URL = config.apiUrl

// =============================================================================
// Types
// =============================================================================

const partnerApiUserSchema = z.object({
  user_id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  /** Nullable on the profile: an account may have no login email. */
  login_email: z.string().nullable(),
  is_active: z.boolean(),
})

const partnerItemSchema = z.object({
  id: z.string(),
  slug: z.string(),
  display_name: z.string(),
  enabled: z.boolean(),
  link_key_version: z.number(),
  api_user: partnerApiUserSchema.nullable(),
  /** Whether this deployment would currently queue deliveries for this partner. */
  ready_to_deliver: z.boolean(),
  /** What is missing, when it would not. */
  blocked_reason: z.string().nullable(),
  has_adapter: z.boolean(),
  delivered_count: z.number(),
  /** The contact email an administrator set for this partner's leads, if any. */
  contact_email: z.string().nullable(),
  /** The support mailbox, which leads carry when contact_email is unset. */
  default_contact_email: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
})

const partnerListResponseSchema = z.object({
  partners: z.array(partnerItemSchema),
})

const eligibleAccountSchema = z.object({
  user_id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  login_email: z.string().nullable(),
  /** The partner already using this account, when one does. */
  serving_partner_slug: z.string().nullable(),
})

const eligibleAccountsResponseSchema = z.object({
  accounts: z.array(eligibleAccountSchema),
})

const partnerDeliveryResultSchema = z.object({
  delivery_id: z.string(),
  partner_slug: z.string(),
  property_id: z.string(),
  /** The delivery task's word for the attempt: sent, retrying or failed. */
  outcome: z.string(),
  status: z.string(),
  attempts: z.number(),
  last_error: z.string().nullable(),
  partner_reference: z.string().nullable(),
  sent_at: z.string().nullable(),
  /** A link equivalent to the one the partner received. It is a credential. */
  link_url: z.string(),
})

export type PartnerApiUser = z.infer<typeof partnerApiUserSchema>
export type PartnerItem = z.infer<typeof partnerItemSchema>
export type EligibleAccount = z.infer<typeof eligibleAccountSchema>
export type PartnerDeliveryResult = z.infer<typeof partnerDeliveryResultSchema>

export interface PartnerUpdate {
  enabled?: boolean
  api_user_id?: string
  /** Null clears it, so leads carry the support mailbox again. */
  contact_email?: string | null
}

// =============================================================================
// Calls
// =============================================================================

function authHeader(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}` }
}

/**
 * Turn a failed response into a message that says what actually went wrong.
 *
 * A 403 here is almost never the person's fault. These endpoints are
 * administrator-only, so a signed-in administrator seeing one usually means the
 * Partner authorization policy is missing from the environment Cerbos reads —
 * which is deny-by-default, so an unknown resource refuses everyone. Saying
 * "could not load" sends people looking for a data problem instead.
 */
async function failureMessage(response: Response, fallback: string): Promise<string> {
  const body = (await response.json().catch(() => null)) as {
    detail?: string | { msg: string }[]
  } | null
  // A 422 from request validation carries a list of field errors, not a sentence.
  if (Array.isArray(body?.detail)) {
    return body.detail.map((error) => error.msg).join(' ')
  }
  if (body?.detail) {
    return body.detail
  }
  if (response.status === 403) {
    return 'Not authorized to manage partners. If you are an administrator, the Partner authorization policy is probably missing from this environment.'
  }
  if (response.status === 401) {
    return 'Your session has expired. Sign in again.'
  }
  return fallback
}

export async function listPartners(token: string): Promise<PartnerItem[]> {
  const response = await fetch(`${API_BASE_URL}/admin/partners`, { headers: authHeader(token) })
  if (!response.ok) {
    throw new Error(await failureMessage(response, 'Could not load partners.'))
  }
  return partnerListResponseSchema.parse(await response.json()).partners
}

export async function getPartner(token: string, partnerId: string): Promise<PartnerItem> {
  const response = await fetch(`${API_BASE_URL}/admin/partners/${partnerId}`, { headers: authHeader(token) })
  if (!response.ok) {
    throw new Error(await failureMessage(response, 'Could not load that partner.'))
  }
  return partnerItemSchema.parse(await response.json())
}

export async function updatePartner(
  token: string,
  partnerId: string,
  changes: PartnerUpdate,
): Promise<PartnerItem> {
  const response = await fetch(`${API_BASE_URL}/admin/partners/${partnerId}`, {
    method: 'PATCH',
    headers: { ...authHeader(token), 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  })
  if (!response.ok) {
    throw new Error(await failureMessage(response, 'Could not save that change.'))
  }
  return partnerItemSchema.parse(await response.json())
}

/**
 * Withdraw every link already delivered to this partner.
 *
 * There is no partial rotation and no way back. Callers must confirm first.
 */
export async function rotatePartnerKey(token: string, partnerId: string): Promise<PartnerItem> {
  const response = await fetch(`${API_BASE_URL}/admin/partners/${partnerId}/rotate-key`, {
    method: 'POST',
    headers: authHeader(token),
  })
  if (!response.ok) {
    throw new Error(await failureMessage(response, 'Could not rotate the key version.'))
  }
  return partnerItemSchema.parse(await response.json())
}

export async function listEligibleAccounts(token: string, search?: string): Promise<EligibleAccount[]> {
  const query = search ? `?search=${encodeURIComponent(search)}` : ''
  const response = await fetch(`${API_BASE_URL}/admin/partners/eligible-accounts${query}`, {
    headers: authHeader(token),
  })
  if (!response.ok) {
    throw new Error(await failureMessage(response, 'Could not load accounts.'))
  }
  return eligibleAccountsResponseSchema.parse(await response.json()).accounts
}

/**
 * Queue the property for the partner and send it now.
 *
 * Resolves whatever the partner answered: the result's `outcome` says whether it
 * was sent, left for the task to retry, or refused for good. Rejects only when
 * the request itself was refused or the delivery could not be recorded.
 */
export async function deliverPropertyToPartner(
  token: string,
  partnerId: string,
  propertyId: string,
): Promise<PartnerDeliveryResult> {
  const response = await fetch(`${API_BASE_URL}/admin/partners/${partnerId}/deliveries`, {
    method: 'POST',
    headers: { ...authHeader(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ property_id: propertyId }),
  })
  if (!response.ok) {
    throw new Error(await failureMessage(response, 'Could not deliver the property.'))
  }
  return partnerDeliveryResultSchema.parse(await response.json())
}
